import { useMemo } from 'react'
import { router, usePage } from '@inertiajs/react'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { reportsMonthlyPath } from '@/shared/lib/routes'
import DataPagination from '@/shared/ui/DataPagination'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type WebInstrumentTaskReport from '@/shared/types/web/WebInstrumentTaskReport'
import { formatMonthYearLabel } from '@/shared/lib/tenantDateTime'
import ReportFilters from './components/ReportFilters'
import ReportMonthGroup from './components/ReportMonthGroup'
import type { MonthlyPeriodSlot } from './components/ReportMonthPeriodSlotRow'
import { ReportEmptyState } from './components/ReportEmptyState'

interface SelectOption {
  value: number
  label: string
  siteId?: number | null
}

interface ReportFiltersShape {
  siteId?: string
  departmentId?: string
  monthFrom?: string
  monthTo?: string
  showAll?: string
  myQueue?: string
}

interface Props {
  records: WebInstrumentTaskReport[]
  meta: PaginationMeta
  filters: ReportFiltersShape
  canShowAll: boolean
  siteOptions: SelectOption[]
  departmentOptions: SelectOption[]
  periodSlots: MonthlyPeriodSlot[]
  [key: string]: unknown
}

interface DisplayMonthGroup {
  key: string
  label: string
  records: WebInstrumentTaskReport[]
  periodSlots: MonthlyPeriodSlot[]
}

/** YYYY-MM from ISO date string (avoids TZ shifting the calendar month). */
function toMonthKey(iso: string): string {
  const head = iso.slice(0, 7)
  if (/^\d{4}-\d{2}$/.test(head)) return head
  const date = new Date(iso)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function buildDisplayMonthGroups(
  records: WebInstrumentTaskReport[],
  slots: MonthlyPeriodSlot[],
): DisplayMonthGroup[] {
  const map = new Map<
    string,
    { records: WebInstrumentTaskReport[]; periodSlots: MonthlyPeriodSlot[] }
  >()

  const ensure = (key: string) => {
    if (!map.has(key)) {
      map.set(key, { records: [], periodSlots: [] })
    }
    return map.get(key)!
  }

  for (const record of records) {
    if (record.reportStartDate == null) continue
    const key = toMonthKey(record.reportStartDate)
    ensure(key).records.push(record)
  }
  for (const slot of slots) {
    const key = toMonthKey(slot.reportStartDate)
    ensure(key).periodSlots.push(slot)
  }

  const keys = [...map.keys()].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))

  return keys.map((key) => {
    const entry = map.get(key)!
    return {
      key,
      label: formatMonthYearLabel(key),
      records: entry.records,
      periodSlots: entry.periodSlots,
    }
  })
}

const ReportsIndexPage = () => {
  const {
    records,
    meta,
    filters,
    canShowAll,
    siteOptions,
    departmentOptions,
    periodSlots = [],
  } = usePage<Props>().props

  useBreadcrumbs([{ label: t('nav.monthlyReports') }])

  const displayMonthGroups = useMemo(
    () => buildDisplayMonthGroups(records, periodSlots),
    [records, periodSlots],
  )

  const goToPage = (page: number) => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', String(page))
    router.visit(`${reportsMonthlyPath()}?${params.toString()}`, { preserveState: true })
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="min-w-0 text-xl font-bold sm:text-2xl">{t('reports.monthly.title')}</h1>
        <div className="lg:hidden">
          <ReportFilters
            filters={filters}
            siteOptions={siteOptions}
            departmentOptions={departmentOptions}
            canShowAll={canShowAll}
            triggerOnly
          />
        </div>
      </div>

      <ReportFilters
        filters={filters}
        siteOptions={siteOptions}
        departmentOptions={departmentOptions}
        canShowAll={canShowAll}
        desktopOnly
      />

      {displayMonthGroups.length === 0 ? (
        <ReportEmptyState message={t('reports.monthly.noReports')} />
      ) : (
        <div className="flex flex-col gap-3">
          {displayMonthGroups.map((group, index) => (
            <ReportMonthGroup
              key={group.key}
              label={group.label}
              records={group.records}
              periodSlots={group.periodSlots}
              defaultOpen={index === 0}
            />
          ))}
        </div>
      )}

      <DataPagination
        meta={meta}
        onPageChange={goToPage}
        noun={t('reports.monthly.paginationRecords')}
      />
    </div>
  )
}

export default ReportsIndexPage
